import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'
import type { RevenueResponse } from '../../../server/services/bi-report-service/index.types'
import { useAnalytics } from '../../analytics/useAnalytics'
import { getOutbrainWidgetIdForPlacement } from '../../../utils/ad-ops/adFunctions'
import { storage as localStorage } from '../../../utils/storage'
import debug from '../../../utils/console'

const logger = debug.withTag('useCpms')
const CPMS_LOCAL_STORAGE_KEY = 'app:ad-cpms'

export interface LocalStorageCpmData {
  date: string
  prices: Record<string, RevenueResponse>
}

function getCurrentDateString(): string {
  return new Date().toISOString().split('T')[0]
}

function getUtcHourOfDay(): string {
  return new Date().getUTCHours().toString().padStart(2, '0')
}

function getDefaultCpm(): number {
  return Number(useAppConfig().defaultCpm)
}

function loadFromLocalStorage(): LocalStorageCpmData | null {
  try {
    const raw = localStorage.getItem(CPMS_LOCAL_STORAGE_KEY)
    if (!raw) return null

    const parsed: LocalStorageCpmData = JSON.parse(raw)
    return parsed.date === getCurrentDateString() ? parsed : null
  } catch (error) {
    logger.error('Error reading stored data from localStorage:', error)
    localStorage.removeItem(CPMS_LOCAL_STORAGE_KEY)
    return null
  }
}

function saveToLocalStorage(data: LocalStorageCpmData): void {
  try {
    localStorage.setItem(CPMS_LOCAL_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    logger.error('Error saving data to localStorage:', error)
  }
}

async function fetchPlacementData(): Promise<Record<string, RevenueResponse>> {
  const dateStr = getCurrentDateString()
  const url = `/api/v2/ad-placement/cpm?date=${dateStr}`
  return await $fetch<Record<string, RevenueResponse>>(url)
}

function getCachedCpm(
  data: LocalStorageCpmData | null,
  placement: string,
  hourOfDay: string
): number | undefined {
  if (!data) return undefined
  const cpmValue = data.prices?.[hourOfDay]?.cpms?.[placement]
  return cpmValue == null ? undefined : Number(cpmValue)
}

function fallbackCpm(placement: string): number {
  const { track } = useAnalytics()
  track('NtpDefaultCpm_Used', {
    placement,
    country: window.ayManagerEnv?.geoInfo?.country ?? '',
  })
  return getDefaultCpm()
}

function createCpmFetcher() {
  const cachedData = ref<LocalStorageCpmData | null>(null)
  let currentFetchPromise: Promise<Record<string, RevenueResponse>> | null =
    null
  let currentFetchDate: string | null = null

  // In-memory Cache
  function getFromCache(placement: string): Promise<number | undefined> {
    if (cachedData.value) {
      const cpm = getCachedCpm(cachedData.value, placement, getUtcHourOfDay())
      return Promise.resolve(cpm !== undefined ? Number(cpm) : undefined)
    }
    return Promise.resolve(undefined)
  }

  // Local Storage
  function getFromLocalStorage(placement: string): Promise<number | undefined> {
    const localData = loadFromLocalStorage()
    if (localData) {
      cachedData.value = localData
      const cpm = getCachedCpm(localData, placement, getUtcHourOfDay())
      return Promise.resolve(cpm !== undefined ? Number(cpm) : undefined)
    }
    return Promise.resolve(undefined)
  }

  // API fetch (only once per day)
  async function getFromAPI(placement: string): Promise<number | undefined> {
    const today = getCurrentDateString()

    async function updateCache(): Promise<Record<string, RevenueResponse>> {
      const prices = await fetchPlacementData()
      const updatedCache: LocalStorageCpmData = { date: today, prices }
      cachedData.value = updatedCache
      saveToLocalStorage(updatedCache)
      return prices
    }

    if (!currentFetchPromise || currentFetchDate !== today) {
      currentFetchDate = today
      currentFetchPromise = updateCache()
    }

    try {
      await currentFetchPromise
      if (cachedData.value) {
        const cpm = getCachedCpm(cachedData.value, placement, getUtcHourOfDay())
        return cpm !== undefined ? Number(cpm) : undefined
      }
    } catch (error) {
      logger.error('Error fetching placement data:', error)
      return undefined
    }

    return undefined
  }

  async function getHourlyCpmForPlacement(placement: string): Promise<number> {
    // Map the placement to the correct widgetId
    const widgetId = getOutbrainWidgetIdForPlacement(placement)
    if (!widgetId) {
      return fallbackCpm(placement)
    }

    const handlers = [getFromCache, getFromLocalStorage, getFromAPI]
    for (const handler of handlers) {
      const result = await handler(widgetId)
      if (result !== undefined) return result
    }
    return fallbackCpm(placement)
  }

  return { getHourlyCpmForPlacement }
}

function useCpmsRaw() {
  const fetcher = createCpmFetcher()
  return { getHourlyCpmForPlacement: fetcher.getHourlyCpmForPlacement }
}

export const useCpms = createSharedComposable(useCpmsRaw)
